import React, { useEffect, useMemo, useRef, useState } from "react"
import Dropdown from "react-bootstrap/Dropdown"
import Modal from "react-bootstrap/Modal"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import Alert from "react-bootstrap/Alert"
import Spinner from "react-bootstrap/Spinner"
import { Link } from "react-router-dom"
import { BoxLabels, BoxLabelsProgressModal } from "../box-labels/index.js"
import { generateBatchReceiptPdf } from "../receiptUtils.jsx"
import { fetchSignatureBlob } from "../utils/fetchSignatureBlob.js"

const DEFAULT_ORDER_DETAIL_PATH = (orderId) => `/v2/orders/${orderId}/order`

/**
 * Shared v2 orders bulk actions — Confirm (optional), Print Receipts, Print Box Labels.
 *
 * @param {{
 *  app: any,
 *  api: any,
 *  helpers?: any,
 *  selectedOrders: any[],
 *  refetchOrdersList?: () => void,
 *  enableBulkConfirm?: boolean,
 *  enableBulkConfirmEmails?: boolean,
 *  disableConfirmWhenOffline?: boolean,
 *  injectBootstrap?: boolean,
 *  orderDetailPath?: (orderId: number | string) => string,
 * }} props
 */
export default function OrdersBulkActions({
    app,
    api,
    helpers = {},
    selectedOrders,
    refetchOrdersList,
    enableBulkConfirm = true,
    enableBulkConfirmEmails = true,
    disableConfirmWhenOffline = false,
    injectBootstrap = false,
    orderDetailPath = DEFAULT_ORDER_DETAIL_PATH,
}) {
    const [showModal, setShowModal] = useState(false)
    const [staffInitial, setStaffInitial] = useState("")
    const [confirmValidationLoading, setConfirmValidationLoading] = useState(false)
    const [confirmOrderValidations, setConfirmOrderValidations] = useState([])
    const [sendConfirmEmails, setSendConfirmEmails] = useState(true)

    const [renderBoxLabelsPdf, setRenderBoxLabelsPdf] = useState(false)
    const [ordersToPrintLabels, setOrdersToPrintLabels] = useState([])
    const [boxLabelProgress, setBoxLabelProgress] = useState(0)
    const [showBoxLabelsModal, setShowBoxLabelsModal] = useState(false)
    const cancelBoxLabelsRef = useRef(false)

    const selectedIds = useMemo(
        () => (selectedOrders ?? []).map((order) => order?.id).filter(Boolean),
        [selectedOrders]
    )
    const selectedCount = selectedIds.length
    const confirmDisabledBecauseOffline = Boolean(disableConfirmWhenOffline && app?.connected === false)
    const confirmDisabled = selectedCount === 0 || confirmDisabledBecauseOffline
    const formatTimeDashes = helpers?.formatTimeDashes ?? ((date) => String(date ?? ""))
    const validateConfirmOrder = helpers?.validateConfirmOrder ?? (() => [false, [], null])
    const contactEmailMethods = api?.CONTACT_EMAIL ?? 1

    useEffect(() => {
        if (!showBoxLabelsModal) {
            setRenderBoxLabelsPdf(false)
            setOrdersToPrintLabels([])
            setBoxLabelProgress(0)
        }
    }, [showBoxLabelsModal])

    useEffect(() => {
        if (!showBoxLabelsModal || boxLabelProgress !== 100) {
            return
        }
        const timeoutId = window.setTimeout(() => setShowBoxLabelsModal(false), 500)
        return () => window.clearTimeout(timeoutId)
    }, [boxLabelProgress, showBoxLabelsModal])

    function handleCloseModal() {
        setStaffInitial("")
        setShowModal(false)
        setConfirmValidationLoading(false)
        setConfirmOrderValidations([])
    }

    function onStaffInitialInput(event) {
        setStaffInitial(String(event.currentTarget.value ?? "").toUpperCase())
    }

    async function fetchSignatureBlobForOrder(order) {
        const url = helpers?.getSignatureImageUrl?.(order)
        return fetchSignatureBlob(url, {
            orderId: order?.id,
            context: "OrdersBulkActions",
        })
    }

    function openConfirmModal() {
        if (!enableBulkConfirm || confirmDisabledBecauseOffline || selectedCount === 0) {
            return
        }

        setConfirmOrderValidations([])
        setConfirmValidationLoading(true)
        if (enableBulkConfirmEmails) {
            setSendConfirmEmails(true)
        }
        setShowModal(true)

        Promise.all(selectedIds.map((id) => api.getOrder(id)))
            .then((fullOrders) => {
                const unconfirmed = (fullOrders ?? []).filter((order) => {
                    const raw = String(order?.status ?? "").toLowerCase()
                    return raw === "" || raw === "new"
                })
                return Promise.all(unconfirmed.map((order) => Promise.resolve(validateConfirmOrder(order))))
            })
            .then((orderValidations) => {
                setConfirmOrderValidations(orderValidations)
                setConfirmValidationLoading(false)
            })
            .catch((error) => {
                console.error(error)
                setConfirmValidationLoading(false)
            })
    }

    async function onConfirmClick() {
        const validOrders = confirmOrderValidations
            .filter(([invalid]) => !invalid)
            .map(([_invalid, _missing, order]) => order)

        const confirmedOrders = []
        for (const order of validOrders) {
            try {
                await api.confirmOrder(order.id, staffInitial)
                confirmedOrders.push(order)
            } catch (error) {
                console.error(error)
            }
        }

        // OMS: optional email send (upload receipt + sendConfirmations).
        // Lodge: upload receipt only so OMS can email after sync — never call sendConfirmations.
        const shouldSendEmails = enableBulkConfirmEmails && sendConfirmEmails
        const shouldUploadReceipts = shouldSendEmails || !enableBulkConfirmEmails
        if (shouldUploadReceipts) {
            const receiptPromises = confirmedOrders.map((order) =>
                api.getOrder(order.id)
                    .then(async (fresh) => {
                        const signatureBlob = await fetchSignatureBlobForOrder(fresh)
                        const pdfBytes = await generateBatchReceiptPdf(
                            [{ order: fresh, master: fresh.master, signatureBlob }],
                            app.locations,
                            app.species,
                            app.options,
                            app.weighInProcesses
                        )
                        const file = new Blob([pdfBytes], { type: "application/pdf" })
                        const body = await api.uploadFile(order.id, file, `email-receipt-${formatTimeDashes(new Date())}.pdf`)
                        if (!shouldSendEmails) {
                            return body
                        }
                        const filename = typeof body === "string" ? body : body?.data
                        return api.sendConfirmations(order.id, filename, contactEmailMethods)
                    })
                    .catch((error) => console.error(error))
            )
            await Promise.allSettled(receiptPromises)
        }

        refetchOrdersList?.()
        handleCloseModal()
    }

    function onBatchPrintReceiptsClicked() {
        if (selectedCount === 0) {
            return
        }

        Promise.all(
            selectedIds.map((id) =>
                api.getOrder(id).then(async (order) => {
                    const orderConfig = { order, master: order.master, signatureBlob: null }
                    const signatureBlob = await fetchSignatureBlobForOrder(order)
                    if (signatureBlob) {
                        orderConfig.signatureBlob = signatureBlob
                    }
                    return orderConfig
                })
            )
        ).then(async (orderConfigs) => {
            const pdfBytes = await generateBatchReceiptPdf(
                orderConfigs,
                app.locations,
                app.species,
                app.options,
                app.weighInProcesses
            )
            const file = new Blob([pdfBytes], { type: "application/pdf" })
            window.open(URL.createObjectURL(file))
        })
    }

    function onBatchPrintLabelsClicked() {
        if (selectedCount === 0) {
            return
        }

        setBoxLabelProgress(0)
        cancelBoxLabelsRef.current = false
        setShowBoxLabelsModal(true)
        setOrdersToPrintLabels([...(selectedOrders ?? [])])
        window.setTimeout(() => setRenderBoxLabelsPdf(true), 200)
    }

    function handleCancelBoxLabels() {
        cancelBoxLabelsRef.current = true
        setRenderBoxLabelsPdf(false)
        setShowBoxLabelsModal(false)
    }

    const numValidOrders = confirmOrderValidations.filter(([invalid]) => !invalid).length
    const anyInvalid = confirmOrderValidations.some(([invalid]) => Boolean(invalid))
    const unconfirmedIds = confirmOrderValidations.map(([_invalid, _missing, order]) => order?.id).filter(Boolean)

    return (
        <>
            <BoxLabels
                orders={ordersToPrintLabels}
                locations={app.locations}
                openPdf={renderBoxLabelsPdf}
                setOpenPdf={setRenderBoxLabelsPdf}
                progress={boxLabelProgress}
                setProgress={setBoxLabelProgress}
                helpers={helpers}
                cancelRef={cancelBoxLabelsRef}
                app={app}
            />
            <BoxLabelsProgressModal
                show={showBoxLabelsModal}
                progress={boxLabelProgress}
                onCancel={handleCancelBoxLabels}
            />

            {enableBulkConfirm ? (
                <Modal show={showModal} onHide={handleCloseModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Orders</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="d-flex" style={{ minHeight: "200px" }}>
                        {confirmValidationLoading ? (
                            <div className="w-100 d-flex flex-column align-items-center justify-content-center">
                                <span className="mb-4">Validating orders...</span>
                                <Spinner animation="border" role="status" style={{ height: "50px", width: "50px" }}>
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            </div>
                        ) : (
                            <div className="w-100">
                                <p>By confirming these orders, you verify that the order details have been reviewed and are correct.</p>
                                <p>Confirmed orders are automatically added to a manifest.</p>

                                <Form.Group className="mb-3 d-flex align-items-end">
                                    <Form.Label>
                                        Staff Initial:<span className="text-danger">&nbsp;*</span>
                                    </Form.Label>
                                    <Form.Control
                                        className="ms-3"
                                        value={staffInitial}
                                        onInput={onStaffInitialInput}
                                        style={{ width: "100px" }}
                                        required
                                    />
                                </Form.Group>

                                {enableBulkConfirmEmails ? (
                                    <Form.Group className="mb-3 d-flex align-items-center">
                                        <Form.Label className="my-0 py-0">Send confirmation emails?</Form.Label>
                                        <Form.Check className="my-0 py-0 ms-3" type="checkbox" style={{ fontSize: "25px" }}>
                                            <Form.Check.Input
                                                checked={sendConfirmEmails}
                                                onChange={(event) => setSendConfirmEmails(event.currentTarget.checked)}
                                            />
                                        </Form.Check>
                                    </Form.Group>
                                ) : null}

                                {unconfirmedIds.length === 0 ? (
                                    <Alert variant="danger">No unconfirmed orders have been selected.</Alert>
                                ) : unconfirmedIds.length < selectedCount ? (
                                    <Alert variant="info">
                                        {selectedCount - unconfirmedIds.length} orders are already confirmed and will be skipped.
                                    </Alert>
                                ) : null}

                                {confirmOrderValidations
                                    .filter(([invalid]) => invalid)
                                    .map(([_invalid, missingFields, invalidOrder]) => (
                                        <Alert
                                            key={invalidOrder?.id ?? invalidOrder?.master}
                                            className="my-2"
                                            variant="danger"
                                            style={{ fontSize: "13px" }}
                                        >
                                            Order{" "}
                                            <Link className="text-black" to={orderDetailPath(invalidOrder.id)} target="_blank">
                                                {invalidOrder?.master}
                                            </Link>{" "}
                                            is missing fields: {(missingFields ?? []).join(", ")}
                                        </Alert>
                                    ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={onConfirmClick}
                            disabled={!staffInitial.trim() || confirmValidationLoading || unconfirmedIds.length === 0 || numValidOrders === 0}
                        >
                            {anyInvalid ? `Confirm Valid Orders (${numValidOrders})` : `Confirm Orders (${numValidOrders})`}
                        </Button>
                    </Modal.Footer>
                </Modal>
            ) : null}

            <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" className="shadow-sm">
                    Actions{selectedCount ? ` (${selectedCount})` : ""}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {enableBulkConfirm ? (
                        confirmDisabledBecauseOffline ? (
                            <span className="d-block" title="Cannot confirm orders while offline.">
                                <Dropdown.Item disabled onClick={openConfirmModal}>
                                    Confirm ({selectedCount})
                                </Dropdown.Item>
                            </span>
                        ) : (
                            <Dropdown.Item disabled={confirmDisabled} onClick={openConfirmModal}>
                                Confirm ({selectedCount})
                            </Dropdown.Item>
                        )
                    ) : null}
                    <Dropdown.Item className={enableBulkConfirm ? "border-top" : undefined} disabled={selectedCount === 0} onClick={onBatchPrintReceiptsClicked}>
                        Print Receipts ({selectedCount})
                    </Dropdown.Item>
                    <Dropdown.Item className="border-top" disabled={selectedCount === 0} onClick={onBatchPrintLabelsClicked}>
                        Print Box Labels ({selectedCount})
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </>
    )
}
